<template>
  <div class='container'>
    <div class='columns'>
      <div class='column'>
        <h1 :class='$style.title'>p s m</h1>
      </div>
      <div :class='["column", $style.nav]'>
        <router-link
          v-for='item in navItems'
          :key='item.name'
          :to='{ name: item.name }'
          :class='$style.navItem'
        >
          {{ item.title }}
        </router-link>
      </div>
    </div>
    <router-view :key='$route.fullPath' />
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, provide } from 'vue';
import { getPermissions } from '@/request';
import { useRouter, isNavigationFailure, NavigationFailureType } from 'vue-router';

const ALLOWED_ORIGINS = [
  'https://kws.internal.knowbe4.com',
  'https://kws-staging.internal.knowbe4.com',
  'http://localhost:3000',
];

const isOriginAllowed = origin =>
  ALLOWED_ORIGINS.includes(origin) ||
  origin.endsWith('.devex-dev.private.knowbe4.com'); // OD Backstage URLs: {od_name}-kws.devex-dev.private.knowbe4.com

const LINKS = {
  home: {
    title: 'Home',
    name: 'EmailSearch'
  },

  smarthosts: {
    title: 'Smarthosts',
    name: 'Smarthosts'
  },

  domainKeys: {
    title: 'Domain Keys',
    name: 'DomainKeys'
  },

  blockedRecipients: {
    title: 'Blocked Recipients',
    name: 'BlockedRecipients'
  }
};

export default {
  name: 'app',
  
  setup() {
    const router = useRouter();
    const permissions = ref([]);

    const waitForBackstageJwt = () => new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onInit);
        reject(new Error('Timed out waiting for Backstage JWT'));
      }, 10000);

      const onInit = event => {
        if (!isOriginAllowed(event.origin)) return;
        if (event.data?.type === 'BACKSTAGE_INIT') {
          clearTimeout(timeoutId);
          sessionStorage.setItem('backstage_parent_origin', event.origin);
          window.removeEventListener('message', onInit);
          if (event.data.jwt) {
            sessionStorage.setItem('backstage_jwt', event.data.jwt);
          } else {
            console.warn('[PSM] BACKSTAGE_INIT received without JWT, falling back to SAML mode');
          }
          if (event.data.currentPath && event.data.currentPath !== '/') {
            const navPath = event.data.currentPath.startsWith('/') ? event.data.currentPath : `/${event.data.currentPath}`;
            router.push(navPath).catch(err => {
              if (!isNavigationFailure(err, NavigationFailureType.duplicated)) {
                console.warn('[PSM] Navigation error:', err);
              }
            });
          }
          resolve();
        }
      };
      window.addEventListener('message', onInit);
    });

    const handleMessage = event => {
      if (!isOriginAllowed(event.origin)) return;
      if (event.data?.type === 'TOKEN_REFRESH' && event.data.jwt) {
        sessionStorage.setItem('backstage_jwt', event.data.jwt);
      } else if (event.data?.type === 'NAVIGATION_REQUEST' && event.data.path) {
        router.push(event.data.path).catch(err => {
          if (!isNavigationFailure(err, NavigationFailureType.duplicated)) {
            console.warn('[PSM] Navigation error:', err);
          }
        });
      }
    };

    watch(() => router.currentRoute.value.fullPath, newPath => {
      const parentOrigin = sessionStorage.getItem('backstage_parent_origin');
      if (parentOrigin && window.parent !== window) {
        window.parent.postMessage({ type: 'URL_CHANGE', source: 'iframe', path: newPath }, parentOrigin);
      }
    });

    onMounted(async () => {
      window.addEventListener('message', handleMessage);

      if (window.parent !== window) {
        // Backstage mode: wait for JWT before making any authenticated requests.
        try {
          await waitForBackstageJwt();
        } catch (err) {
          console.warn('[PSM] No Backstage JWT received, falling back to SAML:', err.message);
        }
      }

      permissions.value = await getPermissions();
    });

    onUnmounted(() => {
      window.removeEventListener('message', handleMessage);
    });

    provide('permissions', permissions);
  },

  computed: {
    navItems() {
      let items = [LINKS.home];
      if (this.$route.name === 'EmailSearch') {
        items = [
          LINKS.smarthosts,
          LINKS.domainKeys,
          LINKS.blockedRecipients
        ];
      }

      return items;
    }
  }
}
</script>

<style lang='scss' module>
@import 'src/styles/variables.scss';
.title {
  color: $brand;
  font-size: 3em;
  margin: 10px 0 25px;
}

.nav {
  margin-top: 44px;
  text-align: right;
}

.navItem {
  color: $link-light;
  font-size: 1.2em;

  &:hover {
    color: $link-hover;

    &:after {
      color: $link;
    }
  }

  &:after {
    color: $link;
    content: '|';
    margin: 0 10px;
  }

  &:last-child {
    &:after {
      content: '';
    }
  }
}
</style>
